/**
 * RequirePermission
 *
 * Route-level guard that wraps a page component and redirects (or renders
 * a fallback) when the current user does not satisfy the required permission.
 *
 * ─── Phase 76.2.D — guard cutover ────────────────────────────────────────────
 * The primary decision source is now `Phase76AuthContext` via the
 * `useIsSuperAdmin` / `useHasPermission` / `useHasModule` hooks. The legacy
 * `usePermissions().can(...)` path is kept as a permissive fallback for the
 * grace window where:
 *   • the Phase 76 context is still hydrating (`!hydrated`), OR
 *   • the Phase 76 cache has no entry yet for the active company (race
 *     between activeCompany switch and the permissions fetch).
 *
 * The Phase 76 path is AUTHORITATIVE once hydrated — it reads directly from
 * `get_my_permissions_for_company()` and `company_has_module()`, both of
 * which are gated by Phase 76 RLS server-side.
 *
 * ─── Important loading behaviour ─────────────────────────────────────────────
 * Company data is fetched asynchronously after login. If we evaluate
 * permissions BEFORE either context completes, every module-gated route
 * would instantly redirect to /403. We wait for whichever source we end up
 * using before rendering a decision.
 *
 * ─── Super-admin note ────────────────────────────────────────────────────────
 * Phase 76's `useIsSuperAdmin()` reads from the `is_super_admin()` RPC
 * (server-pinned to a specific email). Super-admins bypass every route
 * guard. The SIDEBAR continues to use the raw module-active check (no SA
 * bypass) so super-admins see the same nav as the company's users would.
 *
 * ─── Usage ───────────────────────────────────────────────────────────────────
 * Plan-tier guard:
 *   <RequirePermission minPlan="premium" redirectTo="/upgrade">
 *
 * Module + level guard:
 *   <RequirePermission module="cashflow" level="viewer">
 *
 * Company-admin guard:
 *   <RequirePermission requireCompanyAdmin>
 *
 * Super-admin only:
 *   <RequirePermission minPlan="admin" redirectTo="/403">
 *
 * Inline denial (no redirect):
 *   <RequirePermission module="hr" level="editor" redirectTo={null}>
 */

import { ReactNode }         from 'react';
import { Navigate }          from 'react-router-dom';
import { Lock, RefreshCw }   from 'lucide-react';
import { usePermissions }    from '@/hooks/usePermissions';
import {
  useIsSuperAdmin,
  usePhase76Auth,
  usePhase76Hydrated,
}                            from '@/lib/auth';
import type { PermissionRequest, AccessLevel } from '@/lib/rbac';

interface RequirePermissionProps extends PermissionRequest {
  children:    ReactNode;
  /**
   * Where to redirect when access is denied.
   * Defaults to '/403'.  Pass null to show an inline "no access" message.
   */
  redirectTo?: string | null;
}

/**
 * Map a (module, level) pair from the legacy `PermissionRequest` shape into
 * a Phase 76 permission key. The seed convention is:
 *   • viewer            → `${module}.read`
 *   • editor / admin    → `${module}.write`
 * For 'hr' we use the strictest seeded permission for the viewer level
 * (`hr.employee.read.own`) since that is the minimal grant; the .all variant
 * is treated as a stricter check that legacy callers never asked for.
 */
function permissionKeyFor(module: string, level: AccessLevel): string {
  const write = level === 'editor' || level === 'admin';
  if (module === 'hr') {
    return write ? 'hr.employee.write' : 'hr.employee.read.own';
  }
  // cashflow / inventory / future modules all follow the simple convention.
  return write ? `${module}.write` : `${module}.read`;
}

export function RequirePermission({
  children,
  redirectTo = '/403',
  ...permReq
}: RequirePermissionProps) {
  // ── Phase 76 primary source ────────────────────────────────────────────
  const phase76Hydrated = usePhase76Hydrated();
  const isSuperAdmin    = useIsSuperAdmin();
  const {
    activeCompanyId,
    hasPermissionLocal,
    hasModuleLocal,
    permissionsByCompany,
  }                     = usePhase76Auth();

  // ── Legacy fallback ────────────────────────────────────────────────────
  const { can, loadingModules } = usePermissions();

  // ── While EITHER source is still hydrating, show a neutral spinner. ────
  //    We don't want to flash a /403 redirect for a user who, once data
  //    arrives, will turn out to have access.
  const phase76CacheReady = !!activeCompanyId && !!permissionsByCompany[activeCompanyId];
  const stillHydrating    = (!phase76Hydrated) || loadingModules;

  if (stillHydrating) {
    return (
      <div className="flex items-center justify-center py-24 gap-3 text-gray-400">
        <RefreshCw className="w-5 h-5 animate-spin" />
        <span className="text-sm">טוען הרשאות…</span>
      </div>
    );
  }

  // ── Decision ──────────────────────────────────────────────────────────────
  // 1. Super-admin always passes (Phase 76 source).
  // 2. minPlan / requireCompanyAdmin are still legacy-only concepts → delegate
  //    those branches to `can()` exclusively (Phase 76 doesn't model plan tier).
  // 3. module+level checks are Phase 76 authoritative when its cache is ready;
  //    fall through to the legacy `can()` otherwise to avoid a false-negative
  //    blink during the grace window.
  let granted = false;

  if (isSuperAdmin) {
    granted = true;
  } else if (permReq.minPlan || permReq.requireCompanyAdmin) {
    granted = can(permReq);
  } else if (permReq.module) {
    const level = permReq.level ?? 'viewer';
    const permKey = permissionKeyFor(permReq.module as string, level);

    if (phase76CacheReady) {
      // Authoritative path: module must be subscribed AND permission must hold.
      // OR-merge with legacy can() so company admins who have never been
      // explicitly seeded in Phase 76 permission tables still get access.
      const moduleOk = hasModuleLocal(permReq.module as string);
      const permOk   = hasPermissionLocal(permKey);
      granted        = (moduleOk && permOk) || can(permReq);
    } else {
      // Grace window: trust the legacy decision so we don't blink to /403.
      granted = can(permReq);
    }
  } else {
    // No constraint specified → grant.
    granted = true;
  }

  if (!granted) {
    if (redirectTo !== null) {
      return <Navigate to={redirectTo} replace />;
    }
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4 text-gray-400">
        <Lock className="w-12 h-12 text-gray-300" />
        <p className="text-sm font-medium text-gray-500">
          אין לך הרשאה לגשת לדף זה.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}
